import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import type { ImagePullSecret, RepositorySecret, Role, TeamMember, VerifiedDomain, Workspace } from '@partiri-cloud/types'

/**
 * @file workspaces.ts
 * Class for the workspaces client
 */
export class WorkspacesApi {
  private endpoint = '/workspaces'

  constructor(private http: HttpClient) { }

  /**
   * List all workspaces that user belongs to
   * @returns Workspace[]
   */
  public list(): Observable<Workspace[]> {
    return this.http.get<Workspace[]>(this.endpoint)
  }

  /**
   * Requests a single Workspace
   * @param id 
   * @returns Workspace 
   */
  public read(id: string): Observable<Workspace> {
    return this.http.get<Workspace>(`${this.endpoint}/${id}`)
  }

  /**
   * Create a new Workspace to combine services
   * @param Workspace
   */
  public create(workspace: Workspace): Observable<Workspace> {
    return this.http.post<Workspace>(this.endpoint, workspace)
  }

  /**
   * Update the Workspace data by id
   * @param id 
   * @param Workspace
   * @returns Workspace
  */
  public update(id: string, workspace: Workspace): Observable<Workspace> {
    return this.http.put<Workspace>(`${this.endpoint}/${id}`, workspace)
  }

  /**
   * Delete the Workspace
   * @param id 
   */
  public delete(id: string): Observable<boolean> {
    return this.http.delete<boolean>(`${this.endpoint}/${id}`)
  }

  /**
   * Get all teammembers that belong to workspace
   * @param id 
   * @returns 
   */
  public getTeamMembers(id: string): Observable<TeamMember[]> {
    return this.http.get<TeamMember[]>(`${this.endpoint}/team/${id}`)
  }

  /**
   * Add user to team and assign it a role
   * @param workspaceId 
   * @param email 
   * @param role 
   * @returns 
   */
  public addTeamMember(workspaceId: string, email: string, role: string): Observable<void> {
    const team = { workspaceId, email, role }
    return this.http.post<void>(`${this.endpoint}/team`, team)
  }

  /**
   * Remove user from a workspace
   * @param workspaceId 
   * @param email 
   * @param role 
   * @returns 
   */
  public removeTeamMember(workspaceId: string, id: string): Observable<void> {
    return this.http.delete<void>(`${this.endpoint}/team/${workspaceId}/${id}`)
  }

  /**
   * List roles available to associate a user with a workspace
   * @returns roles
   */
  public listRoles(): Observable<Role[]> {
    return this.http.get<Role[]>(`${this.endpoint}/roles`)
  }

  /**
  * SECRETS used to auth registries, repositories and secrets inside the pod
  */

  /**
   * List registry secrets for workspace 
   */
  public listRegistrySecrets(workspaceId: string): Observable<ImagePullSecret[]> {
    return this.http.get<ImagePullSecret[]>(`${this.endpoint}/secrets/registry/${workspaceId}`)
  }

  /**
   * Create a new registry secrets for workspace
   * @param serviceSecret 
   * @returns 
   */
  public createRegistrySecret(secret: ImagePullSecret): Observable<ImagePullSecret> {
    return this.http.post<ImagePullSecret>(`${this.endpoint}/secrets/registry`, secret)
  }

  /**
   * Delete registry secrets for workspace
   * @param serviceSecretId 
   * @returns 
   */
  public deleteRegistrySecret(secretId: string): Observable<boolean> {
    return this.http.delete<boolean>(`${this.endpoint}/secrets/registry/${secretId}`)
  }

  /**
   * List repository secrets for workspace 
   */
  public listRepositorySecrets(workspaceId: string): Observable<RepositorySecret[]> {
    return this.http.get<RepositorySecret[]>(`${this.endpoint}/secrets/repository/${workspaceId}`)
  }

  /**
   * Create a new repository secrets for workspace
   * @param serviceSecret 
   * @returns 
   */
  public createRepositorySecret(secret: RepositorySecret): Observable<RepositorySecret> {
    return this.http.post<RepositorySecret>(`${this.endpoint}/secrets/repository`, secret)
  }

  /**
   * Delete repository secrets for workspace
   * @param serviceSecretId 
   * @returns 
   */
  public deleteRepositorySecret(secretId: string): Observable<boolean> {
    return this.http.delete<boolean>(`${this.endpoint}/secrets/repository/${secretId}`)
  }

  /**
   * DOMAINS used to prove workspace ownership of a hostname before it can be
   * used as a service's custom_url
   */

  /**
   * List verified (and pending) domains for a workspace
   */
  public listDomains(workspaceId: string): Observable<VerifiedDomain[]> {
    return this.http.get<VerifiedDomain[]>(`${this.endpoint}/${workspaceId}/domains`)
  }

  /**
   * Start verification of a new domain for a workspace
   * @param domain bare lowercase hostname, no scheme
   */
  public createDomain(workspaceId: string, domain: string): Observable<VerifiedDomain> {
    return this.http.post<VerifiedDomain>(`${this.endpoint}/${workspaceId}/domains`, { domain })
  }

  /**
   * Delete a domain from a workspace
   */
  public deleteDomain(workspaceId: string, domainId: string): Observable<{ ok: boolean }> {
    return this.http.delete<{ ok: boolean }>(`${this.endpoint}/${workspaceId}/domains/${domainId}`)
  }

  /**
   * Trigger a TXT record verification attempt for a pending domain
   */
  public verifyDomain(workspaceId: string, domainId: string): Observable<VerifiedDomain> {
    return this.http.post<VerifiedDomain>(`${this.endpoint}/${workspaceId}/domains/${domainId}/verify`, {})
  }
}
